<template>
  <div class="content">
    <form @submit.prevent="onExport">
      <div class="fields">
        <div class="field-radio">
          <input
            id="exportAll"
            type="radio"
            name="exportAll"
            value="all"
            checked="true"
          />
          <label for="exportAll">
            <span>All boards</span>
          </label>
        </div>
      </div>
      <hr />
      <div class="fields">
        <div class="field-radio">
          <input
            id="csv"
            v-model="spreadsheetType"
            type="radio"
            value="csv"
            :disabled="!isExportCsvSupported ? true : null"
            :checked="spreadsheetType == 'csv'"
          />
          <label for="csv" :disabled="!isExportCsvSupported ? true : null">
            <span>Csv</span>
          </label>
        </div>
        <div class="field-radio">
          <input
            id="xlsx"
            v-model="spreadsheetType"
            :disabled="!isExportXlsSupported ? true : null"
            type="radio"
            value="xlsx"
            :checked="spreadsheetType == 'xlsx'"
          />
          <label
            :disabled="!isExportXlsSupported ? true : null"
            for="xlsx"
            @click="onClickXlsx"
          >
            <span class="text"> Xlsx </span>
            <Badge
              v-if="!isExportXlsSupported && hasBillingPermission"
              class="ml-2"
            >
              PRO
            </Badge>
          </label>
        </div>
      </div>

      <div class="actions">
        <button
          type="submit"
          class="button"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
        >
          <span>EXPORT</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { usePremiumFeature } from '@/composables/usePremiumFeature.js';
import { EVENT, SYS_PERMISSIONS } from '@/constants.js';
import Badge from '@/ui/components/badge/Badge.vue';
import requests from '../../../vuex/requests.js';

export default {
  components: {
    Badge,
  },

  setup() {
    const { hasPermission: hasBillingPermission } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_BILLING_MANAGE],
    });
    const { isSupported: isExportXlsSupported } = usePremiumFeature({
      feature: 'export_xls',
    });

    const { isSupported: isExportCsvSupported } = usePremiumFeature({
      feature: 'export_csv',
    });

    return {
      hasBillingPermission,
      isExportXlsSupported,
      isExportCsvSupported,
    };
  },

  data() {
    return {
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters('user/projects', ['getOpen']),
    ...mapGetters('project/config', ['getExportOptions']),

    spreadsheetType: {
      get() {
        return this.getExportOptions.spreadsheet.type;
      },
      set(value) {
        const { spreadsheet } = _.cloneDeep(this.getExportOptions);
        spreadsheet.type = value;
        this.setExportOptions({
          props: { spreadsheet },
          apply: true,
        });
      },
    },
  },

  methods: {
    ...mapActions('project/config', ['setExportOptions']),

    /**
     * Event handler - The export button has been clicked.
     */
    onExport() {
      this.isLoading = true;

      requests.export
        .spreadsheet({ hash: this.getOpen, type: this.spreadsheetType })
        .then(response => {
          const { file } = response.data;
          window.location.href = `/export/download/${file}`;
        })
        .catch(error => {
          console.error('Export to spreadsheet failed. Error:', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Event handler - The xlsx option is selected
     */
    onClickXlsx() {
      if (this.hasBillingPermission && !this.isExportXlsSupported) {
        emitter.emit(EVENT.LIMIT_MODAL, {
          open: true,
          title: 'The csv export feature is not available to your current plan',
          subtitle: 'Upgrade to any of our plans to unlock csv export feature',
        });
      }
    },
  },
};
</script>
<style scoped lang="scss">
.content {
  padding: 20px;
}

.modal p {
  font-size: 14px;
  line-height: 1.5;
}

.modal ul {
  padding: 0 0 0 20px;
  margin: 0 0 30px 0;
  list-style: none;
  li {
    font-size: 14px;
    margin: 0 0 10px 0;
    &:before {
      content: '\2022'; /* Add content: \2022 is the CSS Code/unicode for a bullet */
      color: #db841e; /* Change the color */
      font-weight: bold; /* If you want it to be bold */
      display: inline-block; /* Needed to add space between the bullet and the text */
      width: 1em; /* Also needed for space (tweak if needed) */
      margin-left: -1em; /* Also needed for space (tweak if needed) */
    }
  }
}

.modal .actions {
  text-align: right;
}
.modal .button {
  padding: 10px 18px;
  border-radius: 3px;
  width: 100%;
  border: none;
  color: #fff;
  background: #db841e;
  cursor: pointer;
  margin: 0;
  display: inline-block;
  width: auto;
  text-align: center;
  position: relative;
  transition: background 0.25s;
  font-size: 14px;

  span {
    white-space: nowrap;
    display: block;
  }

  &.full {
    display: block;
  }

  &.is-loading {
    &:before {
      margin: -12px 0 0 -11px;
      width: 22px;
      height: 22px;
      position: absolute;
      left: 50%;
      top: 50%;
      content: '';
      -webkit-border-radius: 24px;
      -webkit-background-clip: padding-box;
      -moz-border-radius: 24px;
      -moz-background-clip: padding;
      border-radius: 24px;
      background-clip: padding-box;
      border: rgba(255, 255, 255, 0.25) 2px solid;
      border-top-color: #fff;
      animation: animation-rotate 2000ms linear infinite;
      // -webkit-animation: animation-rotate 2000ms linear infinite;
      // -moz-animation: animation-rotate 2000ms linear infinite;
      // -o-animation: animation-rotate 2000ms linear infinite;
    }

    span {
      color: transparent;
    }
  }

  &:disabled {
    background: #464b4c !important;
    color: #8d8d8d !important;
    cursor: unset;
  }

  &:hover {
    background: #ce750d;
  }

  &:active {
    top: 1px;
  }
}

.fields {
  display: flex;
  margin: 0 0 20px 0;
}

[type='radio']:checked,
[type='radio']:not(:checked) {
  position: absolute;
  left: -9999px;
}
[type='radio']:checked + label,
[type='radio']:not(:checked) + label {
  position: relative;
  cursor: pointer;
  padding: 0;
  margin: 0 25px 15px 0;
  font-size: 14px;

  span:nth-child(1) {
    display: inline;
    padding: 0 0 0 30px;

    /* Needs prefixing */
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
}
[type='radio']:checked + label:before,
[type='radio']:not(:checked) + label:before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  margin-top: -9px;
  width: 20px;
  height: 20px;
  border: 1px solid #696d6d;
  border-radius: 100%;
  background: #252929;
}
[type='radio']:checked + label:after,
[type='radio']:not(:checked) + label:after {
  content: '';
  width: 12px;
  height: 12px;
  background: #db841e;
  position: absolute;
  top: 50%;
  margin-top: -5px;
  left: 4px;
  border-radius: 100%;
  -webkit-transition: all 0.2s ease;
  transition: all 0.2s ease;
}
[type='radio']:not(:checked) + label:after {
  opacity: 0;
  -webkit-transform: scale(0);
  transform: scale(0);
}
[type='radio']:checked + label:after {
  opacity: 1;
  -webkit-transform: scale(1);
  transform: scale(1);
}

[type='radio']:not(:checked) + label[disabled] {
  cursor: default;
  &:before {
    border: 1px solid #686b6b;
    background: #313536;
  }
  .text {
    color: #686b6b;
  }
}

.modal {
  hr {
    height: 0;
    border: 0;
    border-top: 1px solid #3a4345;
    margin: 15px 0;
  }
}

.field-radio label span.upgrade {
  background: #db841e;
  border-radius: 4px;
  color: #fff;
  text-transform: uppercase;
  margin: 0 0 0 8px;
  padding: 4px 6px 4px 6px;
  line-height: 1;
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
}

.checkbox {
  margin: 0 0;

  input[type='checkbox'] {
    position: absolute;
    opacity: 0;

    & + label {
      cursor: pointer;
      position: relative;
      border-radius: 3px;
      font-size: 14px;
    }

    & + label:before {
      content: '';
      margin-right: 7px;
      display: inline-block;
      vertical-align: text-top;
      width: 20px;
      height: 20px;
      background: #2c3233;
      border-radius: 3px;
      border: 1px solid #696d6d;
    }

    &:checked + label:before {
      background: #db841e;
      border: 1px solid transparent;
    }

    &:checked + label:after {
      content: '';
      position: absolute;
      left: 5px;
      top: 10px;
      background: white;
      width: 2px;
      height: 2px;
      box-shadow:
        2px 0 0 white,
        4px 0 0 white,
        4px -2px 0 white,
        4px -4px 0 white,
        4px -6px 0 white,
        4px -8px 0 white;
      transform: rotate(45deg);
    }
  }
}

@-webkit-keyframes animation-rotate {
  100% {
    -webkit-transform: rotate(360deg);
  }
}
@-moz-keyframes animation-rotate {
  100% {
    -moz-transform: rotate(360deg);
  }
}
@-o-keyframes animation-rotate {
  100% {
    -o-transform: rotate(360deg);
  }
}
@keyframes animation-rotate {
  100% {
    transform: rotate(360deg);
  }
}
</style>
